import { faMoneyBillTransfer } from '@fortawesome/pro-regular-svg-icons';
import { Stack } from '@mantine/core';
import debounce from 'debounce-promise';
import { when } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, ISetCampaignSpendRequest } from '@feathr/blackbox';
import { CampaignClass, CampaignState } from '@feathr/blackbox';
import {
  Button,
  CardV2 as Card,
  CurrencyEntry,
  DatePicker,
  Icon,
  showError,
  Text,
  toast,
  UniversalSelect,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT, moment, TimeFormat } from '@feathr/hooks';
import { useAccount, useRole } from '@feathr/report_components';

interface ICampaignOption {
  id: string;
  name: string;
}

interface IProps {
  disabled?: boolean;
}

function CampaignSpendCard({ disabled }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const { isGlobal } = useRole();
  const { Campaigns } = useStore();
  const [options, setOptions] = useState<ICampaignOption[]>([]);

  const spendState = useLocalObservable(() => ({
    selectedCampaign: undefined as ICampaignOption | undefined,
    spendAmount: undefined as number | undefined,
    spendDate: moment.utc().format(TimeFormat.isoDate),
    isSubmitting: false,

    setSelectedCampaign(campaign: ICampaignOption | undefined): void {
      spendState.selectedCampaign = campaign;
    },

    setSpendAmount(amount: number | undefined): void {
      spendState.spendAmount = amount;
    },

    setSpendDate(date: string): void {
      spendState.spendDate = date;
    },

    setIsSubmitting(value: boolean): void {
      spendState.isSubmitting = value;
    },

    clear(): void {
      spendState.selectedCampaign = undefined;
      spendState.spendAmount = undefined;
      spendState.spendDate = moment.utc().format(TimeFormat.isoDate);
    },

    get payload(): ISetCampaignSpendRequest {
      return {
        campaign_id: spendState.selectedCampaign?.id ?? '',
        date: spendState.spendDate,
        spend: spendState.spendAmount ?? 0,
      };
    },

    get isValid(): boolean {
      return (
        spendState.selectedCampaign !== undefined &&
        spendState.spendAmount !== undefined &&
        spendState.spendAmount > 0 &&
        !!spendState.spendDate
      );
    },
  }));

  const loadCampaigns = useCallback(
    async (inputValue?: string): Promise<ICampaignOption[]> => {
      const oneMonthAgo = moment.utc().subtract(1, 'month').format(TimeFormat.isoDate);
      const campaigns = Campaigns.list({
        filters: {
          ...(inputValue ? { name__icontains: inputValue } : {}),
          is_archived__ne: true,
          _cls__in: [
            CampaignClass.Affinity,
            CampaignClass.EmailList,
            CampaignClass.Lookalike,
            CampaignClass.MobileGeoFenceRetargeting,
            CampaignClass.MobileGeoFencing,
            CampaignClass.Omni,
            CampaignClass.Search,
            CampaignClass.SeedSegment,
            CampaignClass.Segment,
          ],
          state__ne: CampaignState.Draft,
          date_end__gte: oneMonthAgo,
        },
        only: ['id', 'name'],
        ordering: ['-date_start'],
        pagination: { page: 0, page_size: 20 },
      });
      await when(() => !campaigns.isPending);
      const campaignOptions = campaigns.models.map((campaign: Campaign) => ({
        id: campaign.id,
        name: campaign.name,
      }));
      setOptions(campaignOptions);
      return campaignOptions;
    },
    [Campaigns],
  );

  const debouncedLoadCampaigns = useMemo(
    () => debounce(loadCampaigns, DEFAULT_DEBOUNCE_WAIT),
    [loadCampaigns],
  );

  function handleCampaignSelect(option: ICampaignOption): void {
    spendState.setSelectedCampaign(option);
  }

  function handleCampaignClear(): void {
    spendState.setSelectedCampaign(undefined);
  }

  function handleSpendAmountChange(value: string | number): void {
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    spendState.setSpendAmount(isNaN(numValue) ? undefined : numValue);
  }

  function handleSpendDateChange(date: string | undefined): void {
    if (date) {
      // Extract just the date part (YYYY-MM-DD) from the ISO string
      spendState.setSpendDate(date.split('T')[0]);
    }
  }

  async function handleApplySpend(): Promise<void> {
    if (!spendState.isValid) {
      return;
    }

    spendState.setIsSubmitting(true);
    try {
      const result = await account.setCampaignSpend(spendState.payload);
      toast(
        t('Campaign spend of ${{spend}} applied.', {
          spend: result.spend.toFixed(2),
        }),
        { type: 'success' },
      );
      spendState.clear();
    } catch (error) {
      showError(error, t);
    } finally {
      spendState.setIsSubmitting(false);
    }
  }

  const isDisabled = disabled || spendState.isSubmitting;

  function getButtonTooltip(): string | undefined {
    if (!isGlobal) {
      return t('Global role required');
    }
    if (!spendState.selectedCampaign) {
      return t('Select a campaign');
    }
    if (!spendState.spendAmount || spendState.spendAmount <= 0) {
      return t('Enter a spend amount');
    }
    if (!spendState.spendDate) {
      return t('Select a date');
    }
    return undefined;
  }

  return (
    <Card disabled={isDisabled}>
      <Card.Header
        description={t('Simulate campaign spend to test growth credits media balance deduction.')}
        title={t('Campaign Spend Simulator')}
      />
      <Card.Content addVerticalGap={true}>
        <Stack gap={'md'}>
          <Stack gap={4}>
            <UniversalSelect<ICampaignOption>
              async={true}
              cacheOptions={true}
              defaultOptions={true}
              disabled={isDisabled}
              getOptionLabel={({ name }: ICampaignOption): string => name}
              getOptionValue={({ id }: ICampaignOption): string => id}
              isClearable={true}
              label={t('Campaign')}
              loadOptions={debouncedLoadCampaigns}
              name={'campaign-select'}
              onClear={handleCampaignClear}
              onSelectSingle={handleCampaignSelect}
              placeholder={t('Select a campaign')}
              value={options.find(({ id }) => id === spendState.selectedCampaign?.id)}
            />
            <Text.SmallRegular c={'dimmed'}>
              {t('Select an RTB campaign to simulate spend against.')}
            </Text.SmallRegular>
          </Stack>

          <Stack gap={4}>
            <CurrencyEntry
              disabled={isDisabled}
              label={t('Spend Amount')}
              min={0}
              name={'spend-amount'}
              onChange={handleSpendAmountChange}
              placeholder={t('Enter spend amount (e.g. 500)')}
              value={spendState.spendAmount ?? 0}
            />
            <Text.SmallRegular c={'dimmed'}>
              {t('The amount of simulated spend to apply to the campaign.')}
            </Text.SmallRegular>
          </Stack>

          <Stack gap={4}>
            <DatePicker
              disabled={isDisabled}
              label={t('Spend Date')}
              maxDate={new Date()}
              onDateStrChange={handleSpendDateChange}
              showCalendarIcon={true}
              value={spendState.spendDate}
            />
            <Text.SmallRegular c={'dimmed'}>
              {t('The date the simulated spend will be recorded against.')}
            </Text.SmallRegular>
          </Stack>
        </Stack>
      </Card.Content>
      <Card.Actions>
        <Button
          disabled={!isGlobal || !spendState.isValid || isDisabled}
          onClick={handleApplySpend}
          prefix={<Icon icon={faMoneyBillTransfer} />}
          tooltip={getButtonTooltip()}
          type={'primary'}
        >
          {t('Apply Spend')}
        </Button>
      </Card.Actions>
    </Card>
  );
}

export default observer(CampaignSpendCard);
